import { useEffect, useState } from 'react';
import { apiClient } from '../services/apiClient';
import { defaultLeague, LEAGUES, type LeagueKey } from '../config/leagues';

/** A teaser-sized slice of today's most confident AI pick. */
export interface TodaysCall {
  league: LeagueKey;
  leagueLabel: string;
  /** "BOS @ NYK" */
  matchup: string;
  /** breakdown.theRead (fallback: headline), clamped for a coverline slot. */
  headline: string;
  /** The picked side's consensus moneyline, e.g. "+185" / "-150". */
  oddsLabel: string | null;
}

export interface TodaysCallState {
  call: TodaysCall | null;
  loading: boolean;
  error: boolean;
  noPick: boolean;
}

function formatMoneyline(value: number | undefined): string | null {
  if (value === undefined || Number.isNaN(value)) return null;
  return value > 0 ? `+${value}` : `${value}`;
}

function clamp(text: string, max = 90): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trimEnd()}…`;
}

/**
 * Today's most confident ungraded pick in the default in-season league.
 * Resolves to null on any failure or an empty slate — callers keep a static
 * fallback rendered, so this never blocks or breaks the page.
 */
export function useTodaysCall(enabled: boolean): TodaysCallState {
  const [state, setState] = useState<TodaysCallState>({
    call: null,
    loading: enabled,
    error: false,
    noPick: false,
  });

  useEffect(() => {
    if (!enabled) {
      setState({ call: null, loading: false, error: false, noPick: false });
      return;
    }
    let alive = true;
    setState({ call: null, loading: true, error: false, noPick: false });

    (async () => {
      try {
        const now = new Date();
        const league = defaultLeague(now);
        const startDate = now.toISOString().slice(0, 10);
        const predictions = await apiClient.getPredictions({ league, startDate, endDate: startDate });
        const upcoming = predictions
          .filter((p) => p.status === 'scheduled' || p.status === 'in-progress')
          .sort((a, b) => b.breakdown.confidence - a.breakdown.confidence);
        const top = upcoming[0];
        if (!alive) return;
        if (!top) {
          setState({ call: null, loading: false, error: false, noPick: true });
          return;
        }

        const { breakdown, odds } = top;
        const oddsValue =
          breakdown.winnerSide === 'home' ? odds?.moneylineHome
          : breakdown.winnerSide === 'away' ? odds?.moneylineAway
          : odds?.moneylineDraw;

        setState({
          call: {
            league,
            leagueLabel: LEAGUES.find((l) => l.key === league)?.label ?? league,
            matchup: `${top.awayTeam.abbreviation} @ ${top.homeTeam.abbreviation}`,
            headline: clamp(breakdown.theRead || breakdown.headline),
            oddsLabel: formatMoneyline(oddsValue),
          },
          loading: false,
          error: false,
          noPick: false,
        });
      } catch {
        if (alive) setState({ call: null, loading: false, error: true, noPick: false });
      }
    })();

    return () => {
      alive = false;
    };
  }, [enabled]);

  return state;
}
